import React from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import type { FieldError } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import CheckIcon from '@material-ui/icons/Check';
import InfoIcon from '@material-ui/icons/Info';
import { ValidationError } from 'yup';

import PasswordTextField from 'components/PasswordTextField';
import type { StepComponentProps } from 'components/Stepper';
import {
  setupPasswordValidationSchema as validationSchema,
  passwordValidationSchema,
} from 'containers/shared/validationSchemas';
import getValidationErrors from 'utils/getValidationErrors';
import { useUpdateValidation } from 'components/shared/hooks';

import useStyles from './SetupPassword.style';

let validationErrors: ValidationError['errors'];
getValidationErrors({ password: '' }, passwordValidationSchema).then(
  (errors) => {
    validationErrors = errors;
  },
  (promiseRejectionReason: string) => {
    throw new Error(`error: ${promiseRejectionReason}`);
  }
);

const errorIsCurrent = (
  validationError: string,
  fieldError: FieldError | undefined
): boolean => {
  if (!fieldError) {
    return false; // no errors at all - short circuit
  }
  const { message, types } = fieldError;
  if (validationError === message) {
    return true; // validationError is the current error message
  }
  if (types) {
    const errorTypes = Object.keys(types);
    // check if validationError is in one of the React Hook Form error types
    return errorTypes
      .map((errorType) => {
        const error = types[errorType];
        return (
          (typeof error === 'string' && validationError === error) ||
          (Array.isArray(error) && error.includes(validationError))
        );
      })
      .includes(true);
  }
  return false; // no errors at all
};

type SetupPasswordProps = StepComponentProps & {
  currentUser: string | null;
};

export type SetupPasswordFormValues = { password: string; username: string };

export const isSetupPasswordFormValues = (
  stepValues: unknown
): stepValues is SetupPasswordFormValues =>
  !!(stepValues as SetupPasswordFormValues).password &&
  !!(stepValues as SetupPasswordFormValues).username;

const defaultValues: SetupPasswordFormValues = { password: '', username: '' };

const SetupPassword: React.FC<SetupPasswordProps> = (props) => {
  const { onNext, updateValidation, formId, pending, currentUser } = props;
  const {
    header,
    form,
    validationListItemDense,
    validationListItemGutters,
    validationListItemIcon,
    validationListItemInfoIcon,
    validationListItemCheckIcon,
  } = useStyles();

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm<SetupPasswordFormValues>({
    mode: 'all',
    criteriaMode: 'all',
    defaultValues,
    resolver: yupResolver(validationSchema, { abortEarly: false }),
  });

  useUpdateValidation(updateValidation, isValid);

  // set username field (hidden form value)
  if (currentUser) {
    setValue('username', currentUser);
  }

  // TODO: pass this in as a prop?
  const onSubmit: SubmitHandler<SetupPasswordFormValues> = (values) => {
    if (onNext) {
      onNext(values);
    }
  };

  return (
    <>
      <Typography variant="h2" className={header}>
        {
          "Your email is verified.\nNow let's secure your account\nwith a strong password."
        }
      </Typography>
      <form
        id={formId}
        className={form}
        aria-label="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <PasswordTextField
              variant="outlined"
              margin="normal"
              size="small"
              label="Enter a secure password."
              required
              fullWidth
              disabled={pending}
              error={errors.password?.type === 'required'}
              helperText={
                errors.password?.type === 'required' && errors.password?.message
              }
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...field}
            />
          )}
        />
      </form>
      <List dense>
        {validationErrors.map((validationError, index) => {
          const isCurrentError = isValid
            ? false
            : errorIsCurrent(validationError, errors.password);
          return (
            !!index /* exclude required error */ && (
              <ListItem
                key={validationError}
                dense
                classes={{
                  dense: validationListItemDense,
                  gutters: validationListItemGutters,
                }}
              >
                <ListItemIcon className={validationListItemIcon}>
                  {!isDirty || isCurrentError ? (
                    <InfoIcon className={validationListItemInfoIcon} />
                  ) : (
                    <CheckIcon className={validationListItemCheckIcon} />
                  )}
                </ListItemIcon>
                <ListItemText primary={validationError} />
              </ListItem>
            )
          );
        })}
      </List>
    </>
  );
};

export default SetupPassword;
